import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, ArrowRight } from 'lucide-react';

/**
 * MustChangePasswordBanner
 *
 * Sticky amber strip rendered at the top of every page when the active
 * user has `must_change_password === true` (set by the Pilot setup
 * script via `POST /api/users/{id}/reset-password` with
 * `force_change_on_next_login=true`).
 *
 * Reactivity contract: the banner subscribes to two signals so it
 * disappears the instant the user clears the flag:
 *   1. The native `storage` event — fires when another tab updates
 *      localStorage.
 *   2. A custom `user-updated` event — dispatched by Profile.js after
 *      a successful password change inside the same tab.
 */
const MustChangePasswordBanner = () => {
  const [mustChange, setMustChange] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('user') || 'null');
      return !!stored?.must_change_password;
    } catch { return false; }
  });

  useEffect(() => {
    const refresh = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || 'null');
        setMustChange(!!stored?.must_change_password);
      } catch { setMustChange(false); }
    };
    window.addEventListener('storage', refresh);
    window.addEventListener('user-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('user-updated', refresh);
    };
  }, []);

  if (!mustChange) return null;
  return (
    <div
      data-testid="must-change-password-banner"
      className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-3 text-sm"
    >
      <KeyRound className="w-4 h-4 text-amber-700" />
      <span className="text-amber-900 font-semibold">
        Your password is temporary — please change it to keep your account secure.
      </span>
      <span className="text-amber-700 opacity-60">·</span>
      <span className="text-amber-900 font-semibold" style={{ direction: 'rtl' }}>
        كلمة المرور الحالية مؤقتة — يُرجى تغييرها الآن.
      </span>
      <Link
        to="/profile"
        data-testid="change-password-link"
        className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition"
      >
        Change now <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
};

export default MustChangePasswordBanner;
